import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Table, Users } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { formatCurrency } from "../utils/formatters";
import { COLORS } from "../utils/constants";

// Recap CA Page - Charte graphique Biz360 harmonisée
export const RecapCAPage = () => {
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const api = useApi();
  const { user } = useAuth();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { 
      const response = await api.get("/ventes"); 
      setVentes(response.data.filter(v => v.statut === "payé")); 
    } catch (err) { toast.error("Erreur"); }
    setLoading(false);
  };

  const monthsFr = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  const year = new Date().getFullYear();

  // Helper : date de référence pour le CA = date d'encaissement (régime micro = comptabilité de caisse)
  const getDateComptable = (v) => (v.statut === "payé" && v.date_paiement ? v.date_paiement : v.date);

  // Calculate CA by month and activity — basé sur la date d'encaissement
  const getCAByMonth = (activiteType) => {
    return monthsFr.map((month, i) => {
      const monthKey = `${year}-${String(i + 1).padStart(2, '0')}`;
      const ca = ventes
        .filter(v => getDateComptable(v).startsWith(monthKey) && (v.activite_type || "principale") === activiteType)
        .reduce((sum, v) => sum + v.montant, 0);
      return { month, ca };
    });
  };

  // Calculate CA by client
  const getCAByClient = () => {
    const clientMap = {};
    ventes.forEach(v => {
      if (!clientMap[v.client]) clientMap[v.client] = 0;
      clientMap[v.client] += v.montant;
    });
    return Object.entries(clientMap)
      .map(([client, ca]) => ({ client, ca }))
      .sort((a, b) => b.ca - a.ca);
  };

  const caPrincipale = getCAByMonth("principale");
  const caSecondaire = getCAByMonth("secondaire");
  const totalPrincipale = caPrincipale.reduce((sum, m) => sum + m.ca, 0);
  const totalSecondaire = caSecondaire.reduce((sum, m) => sum + m.ca, 0);
  const caByClient = getCAByClient();

  if (loading) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div></DashboardLayout>);
  }

  const MonthGrid = ({ data, total, title, isPrincipal }) => (
    <div className="rounded-xl border p-6" style={{ 
      backgroundColor: COLORS.bg, 
      borderLeft: `4px solid ${isPrincipal ? COLORS.principale : COLORS.accent}` 
    }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold" style={{ color: isPrincipal ? COLORS.principale : COLORS.accent }}>
          {title}
        </h3>
        <span className="text-2xl font-bold" style={{ color: COLORS.text, fontFamily: 'Calibri, sans-serif' }}>
          {formatCurrency(total)}
        </span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.map((item, i) => (
          <div 
            key={i} 
            className="p-3 rounded-lg"
            style={{ 
              backgroundColor: item.ca > 0 ? (isPrincipal ? COLORS.principale + '15' : COLORS.accent + '15') : '#f5f5f5'
            }}
          >
            <div className="text-xs text-muted-foreground mb-1">{item.month}</div>
            <div 
              className="font-bold"
              style={{ 
                color: item.ca > 0 ? COLORS.text : '#ccc',
                fontFamily: 'Calibri, sans-serif'
              }}
            >
              {item.ca > 0 ? formatCurrency(item.ca) : "- €"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="recap-ca-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: COLORS.text }}>
            <Table size={28} style={{ color: COLORS.principale }} /> Mon CA {year}
          </h1>
          <p className="text-muted-foreground">Vue mensuelle de ton chiffre d'affaires encaissé</p>
        </div>

        {/* Total Global - en premier */}
        <div className="rounded-xl p-6" style={{ 
          background: `linear-gradient(135deg, ${COLORS.principale} 0%, ${COLORS.accent} 100%)` 
        }}>
          <div className="flex items-center justify-between text-white">
            <div>
              <div className="text-sm opacity-90">Total CA Encaissé {year}</div>
              <div className="text-4xl font-bold" style={{ fontFamily: 'Calibri, sans-serif' }}>
                {formatCurrency(totalPrincipale + totalSecondaire)}
              </div>
            </div>
            <div className="text-6xl opacity-30">💰</div>
          </div>
        </div>

        {/* Activité Principale */}
        <MonthGrid data={caPrincipale} total={totalPrincipale} title="ACTIVITÉ PRINCIPALE" isPrincipal={true} />

        {/* Activité Secondaire - only if double activité */}
        {user?.double_activite && (
          <MonthGrid data={caSecondaire} total={totalSecondaire} title="ACTIVITÉ SECONDAIRE" isPrincipal={false} />
        )}

        {/* CA par client */}
        {caByClient.length > 0 && (
          <div className="rounded-xl border p-6" style={{ backgroundColor: COLORS.bg }}>
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: COLORS.accent }}>
              <Users size={20} /> CA PAR CLIENT
            </h3>
            <div className="space-y-2">
              {caByClient.slice(0, 10).map((item, i) => {
                const percent = (item.ca / (totalPrincipale + totalSecondaire)) * 100;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" 
                         style={{ backgroundColor: COLORS.principale + '20', color: COLORS.text }}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-sm">{item.client}</span>
                        <span className="font-bold" style={{ color: COLORS.text, fontFamily: 'Calibri, sans-serif' }}>
                          {formatCurrency(item.ca)}
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(100, percent)}%`,
                            backgroundColor: COLORS.principale
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-12 text-right">{percent.toFixed(0)}%</span>
                  </div>
                );
              })}
              {caByClient.length > 10 && (
                <div className="text-center text-sm text-muted-foreground pt-2">
                  + {caByClient.length - 10} autres clients
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
